import React, { useRef, useEffect, KeyboardEvent, useState } from 'react';
import { EmailFormattingToolbar } from './EmailFormattingToolbar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface EmailRichTextInputProps {
  subject: string;
  onSubjectChange: (subject: string) => void;
  to: string;
  onToChange: (to: string) => void;
  content: string;
  onContentChange: (content: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
}

export function EmailRichTextInput({
  subject,
  onSubjectChange,
  to,
  onToChange,
  content,
  onContentChange,
  onSend,
  placeholder = "Compose your email...",
  disabled = false
}: EmailRichTextInputProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showToolbar, setShowToolbar] = useState(false);

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onContentChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      // Clean up the HTML content to remove unnecessary formatting
      let cleanContent = editorRef.current.innerHTML;
      
      // Convert <div> and <br> to proper line breaks for better formatting
      cleanContent = cleanContent
        .replace(/<div><br><\/div>/g, '<br>')
        .replace(/<div>/g, '<br>')
        .replace(/<\/div>/g, '')
        .replace(/^<br>/, ''); // Remove leading br
      
      onContentChange(cleanContent);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFocus = () => {
    setShowToolbar(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Only hide toolbar if focus is not moving to a toolbar element
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !relatedTarget.closest('[data-toolbar]')) {
      setTimeout(() => setShowToolbar(false), 150);
    }
  };

  return (
    <div className="space-y-3 bg-background border border-border/50 rounded-lg p-4">
      {/* Email Fields */}
      <div className="space-y-3">
        <div>
          <Label htmlFor="to" className="text-sm font-medium">To</Label>
          <Input
            id="to"
            type="email"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
            placeholder="recipient@example.com"
            disabled={disabled}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder="Email subject"
            disabled={disabled}
            className="mt-1"
          />
        </div>
      </div>

      {/* Formatting Toolbar */}
      {showToolbar && (
        <div data-toolbar>
          <EmailFormattingToolbar onFormat={handleFormat} />
        </div>
      )}

      {/* Rich Text Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="min-h-[120px] max-h-60 overflow-y-auto p-3 border border-border/50 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          style={{
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
          data-placeholder={placeholder}
        />
        {!content && (
          <div className="absolute top-3 left-3 text-muted-foreground pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>

      <div className="text-xs text-muted-foreground">
        Press Ctrl+Enter (Cmd+Enter on Mac) to send
      </div>
    </div>
  );
}